/* eslint-disable react-hooks/immutability */
import React, { useState, useEffect } from 'react';
import { Table } from 'lucide-react';
import api from '../../lib/api';

const MONTHS_FR = { '01': 'Jan', '02': 'Fév', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aoû', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Déc' };

function formatMonth(m) {
    const [y, mo] = m.split('-');
    return `${MONTHS_FR[mo] || mo} ${y.slice(2)}`;
}

export default function MonthlyComparison() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(6);

    useEffect(() => { load(); }, [months]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/agency/monthly-comparison?months=${months}`);
            setData(res.data);
        } catch (_e) { /* erreur réseau ignorée */ }
        finally { setLoading(false); }
    };

    if (loading) return <div className="animate-pulse bg-card rounded-2xl h-48 border border-border" />;
    if (!data) return null;

    // Layout en grille CSS (pas de <table>) — évite les warnings de nesting React
    // déclenchés par l'instrumentation qui enveloppe les .map() de cellules.
    const gridStyle = {
        gridTemplateColumns: `minmax(140px, 1.6fr) repeat(${data.months.length}, 38px 38px) 44px 44px 46px`,
    };
    const totalEntered = Object.values(data.agency_totals).reduce((s, v) => s + (v.entered || 0), 0);
    const totalSold = Object.values(data.agency_totals).reduce((s, v) => s + (v.sold || 0), 0);

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="monthly-comparison">
            <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                    <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                        <Table className="w-4 h-4 text-[#681793]" />
                        Comparatif mensuel
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Mandats rentrés / sortis par agent</p>
                </div>
                <div className="flex gap-1">
                    {[3, 6, 12].map(m => (
                        <button key={m} onClick={() => setMonths(m)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${months === m ? 'bg-[#681793]/20 text-[#681793]' : 'text-muted-foreground hover:bg-white/5'}`}
                            data-testid={`months-${m}`}
                        >{m}M</button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-max text-xs" role="table">
                    {/* En-tête ligne 1 : mois */}
                    <div className="grid border-b border-border" style={gridStyle} role="row">
                        <div className="text-left p-3 font-medium text-muted-foreground sticky left-0 bg-card z-10">Agent</div>
                        {data.months.map(m => (
                            <div key={m} className="text-center p-2 font-medium text-muted-foreground" style={{ gridColumn: 'span 2' }}>
                                {formatMonth(m)}
                            </div>
                        ))}
                        <div className="text-center p-2 font-bold text-foreground bg-white/5" style={{ gridColumn: 'span 2' }}>Total</div>
                        <div className="text-center p-2 font-medium text-muted-foreground">Conv.</div>
                    </div>

                    {/* En-tête ligne 2 : IN / OUT */}
                    <div className="grid border-b border-border/50" style={gridStyle} role="row">
                        <div className="sticky left-0 bg-card z-10" />
                        {data.months.map(m => (
                            <React.Fragment key={m}>
                                <div className="text-center px-1 py-1 text-[10px] text-green-400/70 font-normal">IN</div>
                                <div className="text-center px-1 py-1 text-[10px] text-blue-400/70 font-normal">OUT</div>
                            </React.Fragment>
                        ))}
                        <div className="text-center px-1 py-1 text-[10px] text-green-400/70 font-normal bg-white/5">IN</div>
                        <div className="text-center px-1 py-1 text-[10px] text-blue-400/70 font-normal bg-white/5">OUT</div>
                        <div className="text-center px-1 py-1 text-[10px] text-muted-foreground font-normal">%</div>
                    </div>

                    {/* Lignes agents */}
                    {data.agents.map((agent, idx) => (
                        <div
                            key={agent.agent_id}
                            className={`grid border-b border-border/30 hover:bg-white/[0.02] transition-colors ${idx % 2 === 0 ? '' : 'bg-white/[0.01]'}`}
                            style={gridStyle}
                            role="row"
                            data-testid={`agent-row-${agent.agent_id}`}
                        >
                            <div className="p-3 font-medium text-foreground sticky left-0 bg-card z-10 truncate">
                                {agent.name}
                            </div>
                            {data.months.map(m => {
                                const md = agent.monthly[m] || {};
                                return (
                                    <React.Fragment key={m}>
                                        <div className="text-center p-1">
                                            <span className={`inline-block min-w-[20px] ${md.entered > 0 ? 'text-green-400 font-bold' : 'text-white/15'}`}>
                                                {md.entered || '-'}
                                            </span>
                                        </div>
                                        <div className="text-center p-1">
                                            <span className={`inline-block min-w-[20px] ${md.sold > 0 ? 'text-blue-400 font-bold' : 'text-white/15'}`}>
                                                {md.sold || '-'}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                            <div className="text-center p-1 bg-white/5 font-bold text-green-400">{agent.totals.entered}</div>
                            <div className="text-center p-1 bg-white/5 font-bold text-blue-400">{agent.totals.sold}</div>
                            <div className="text-center p-1 flex items-center justify-center">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                    agent.totals.conversion_rate > 30 ? 'bg-green-500/20 text-green-400' :
                                    agent.totals.conversion_rate > 0 ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-white/5 text-white/30'
                                }`}>
                                    {agent.totals.conversion_rate}%
                                </span>
                            </div>
                        </div>
                    ))}

                    {/* Ligne totaux agence */}
                    <div className="grid border-t-2 border-[#681793]/30 bg-[#681793]/5" style={gridStyle} role="row">
                        <div className="p-3 font-bold text-[#681793] sticky left-0 bg-[#681793]/5 z-10">TOTAL AGENCE</div>
                        {data.months.map(m => {
                            const at = data.agency_totals[m] || {};
                            return (
                                <React.Fragment key={m}>
                                    <div className="text-center p-1 font-bold text-green-400">{at.entered || '-'}</div>
                                    <div className="text-center p-1 font-bold text-blue-400">{at.sold || '-'}</div>
                                </React.Fragment>
                            );
                        })}
                        <div className="text-center p-1 bg-white/5 font-bold text-green-400">{totalEntered}</div>
                        <div className="text-center p-1 bg-white/5 font-bold text-blue-400">{totalSold}</div>
                        <div />
                    </div>
                </div>
            </div>

            <div className="p-3 border-t border-border flex items-center gap-4 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> IN = Mandats rentrés</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> OUT = Mandats vendus</span>
                <span className="flex items-center gap-1">Conv. = Taux de conversion</span>
            </div>
        </div>
    );
}
